import { useState, useEffect } from "react";
import {
  Trash2,
  Plus,
  LogOut,
  Edit3,
  ExternalLink,
  Database,
  RefreshCw,
  Eye,
  AlertTriangle,
  X,
  Check,
  FileText,
  Users,
  FolderOpen,
  Loader2,
  CheckCircle2,
  AlertCircle,
  HardDrive,
  Ban,
  Activity,
  BarChart3,
  Cpu,
  MessageSquare,
  Scan,
  Layers,
  Menu,
  PanelLeftClose,
} from "lucide-react";
import {
  login,
  refreshAuth,
  fetchClients,
  fetchConfigDefaults,
  createClient,
  updateClient,
  deleteClient,
  deleteDocument,
  cancelDocument,
  fetchDocuments,
  fetchChunks,
  resyncDatabase,
  wipeDatabase,
  fetchDatabaseOverview,
  clearDatabase,
  fetchErrorLogCount,
  resetClientMetrics,
  setClientPaused,
  fetchGlobalStats,
} from "../../api/admin";
import { clearTokens } from "../../api/client";
import Toast from "../../components/Toast";
import LoadingOverlay from "../../components/LoadingOverlay";
import { useToast } from "../../hooks/useToast";
import { slugify, formatSize, formatApiErrorDetail } from "../../utils/format";
import ClientFormFields from "./ClientFormFields";
import ClientReadView from "./ClientReadView";
import AdminAnalytics from "./AdminAnalytics";
import HeliconeMetrics from "./HeliconeMetrics";
import { EMPTY_FORM, buildNewForm } from "./adminConstants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { brandStyle } from "../../utils/branding";

export default function Admin() {
  const [isAuth, setIsAuth] = useState(false);
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [clients, setClients] = useState([]);
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM); // sera mis à jour via defaultConfig
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [viewingChunks, setViewingChunks] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [globalStats, setGlobalStats] = useState({
    stats_30d: {},
    total_lifetime: {},
  });
  const [errorCount, setErrorCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState("dashboard"); // "dashboard" | "helicone"
  const { toasts, toast, removeToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshAuth();
        const statsRes = await fetchGlobalStats();
        if (cancelled) return;
        setGlobalStats(statsRes.data);

        const clientsRes = await fetchClients();
        if (cancelled) return;
        setClients(clientsRes.data);
        try {
          const defRes = await fetchConfigDefaults();
          if (!cancelled) setDefaultConfig(defRes.data);
        } catch {
          // ignore
        }

        try {
          const errRes = await fetchErrorLogCount();
          if (!cancelled) setErrorCount(errRes.data.count || 0);
        } catch {
          // ignore
        }

        if (!cancelled) setIsAuth(true);
      } catch {
        clearTokens();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(creds.username, creds.password);
      const statsRes = await fetchGlobalStats();
      setGlobalStats(statsRes.data);
      const clientsRes = await fetchClients();
      setClients(clientsRes.data);
      try {
        const defRes = await fetchConfigDefaults();
        setDefaultConfig(defRes.data);
      } catch {
        // ignore
      }

      try {
        const errRes = await fetchErrorLogCount();
        setErrorCount(errRes.data.count || 0);
      } catch {
        // ignore
      }

      setIsAuth(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast("Trop de tentatives. Veuillez patienter une minute.", "error");
      } else {
        toast(formatApiErrorDetail(err, "Identifiants incorrects"), "error");
      }
    }
  };

  const loadClients = async () => {
    const res = await fetchClients();
    setClients(res.data);

    try {
      const errRes = await fetchErrorLogCount();
      setErrorCount(errRes.data.count || 0);
    } catch {
      // ignore
    }
  };

  const loadDocuments = async (id) => {
    const res = await fetchDocuments(id);
    setDocuments(res.data.documents || []);
  };

  const selectClient = (client) => {
    if (selectedId === client.id) {
      setSelectedId(null);
      setDocuments([]);
      setSidebarOpen(false);
      return;
    }
    setSelectedId(client.id);
    setForm(() => ({ ...client }));
    setIsEditing(false);
    loadDocuments(client.id);
    setSidebarOpen(false);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "name" && !isEditing) updated.slug = slugify(value);
      return updated;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createClient(form);
      toast(`Client "${form.name}" créé !`);
      setShowForm(false);
      setForm(EMPTY_FORM);
      loadClients();
    } catch (err) {
      toast(formatApiErrorDetail(err, "Erreur création"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await updateClient(selectedId, form);
      toast("Modifications sauvegardées !");
      setIsEditing(false);
      loadClients();
    } catch (err) {
      toast(formatApiErrorDetail(err, "Erreur mise à jour"), "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Supprimer "${name}" et toutes ses données ?`)) return;
    try {
      await deleteClient(id);
      toast(`"${name}" supprimé`);
      if (selectedId === id) {
        setSelectedId(null);
        setDocuments([]);
      }
      loadClients();
    } catch {
      toast("Erreur suppression", "error");
    }
  };

  const selectedClient = clients.find((c) => c.id === selectedId);

  // --- Sélection multiple clients ---
  const toggleClientSelection = (e, id) => {
    e.stopPropagation();
    setSelectedClientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllClients = () => {
    if (selectedClientIds.length === clients.length && clients.length > 0) {
      setSelectedClientIds([]);
    } else {
      setSelectedClientIds(clients.map((c) => c.id));
    }
  };

  const handleBulkDeleteClients = async () => {
    if (!selectedClientIds.length) return;
    const names = clients
      .filter((c) => selectedClientIds.includes(c.id))
      .map((c) => c.name)
      .join(", ");
    if (
      !confirm(
        `Supprimer ${selectedClientIds.length} entreprise(s) et toutes leurs données ?\n\n${names}`,
      )
    )
      return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedClientIds.map((id) => deleteClient(id)));
      toast(`${selectedClientIds.length} entreprise(s) supprimée(s)`);
      if (selectedClientIds.includes(selectedId)) {
        setSelectedId(null);
        setDocuments([]);
      }
      setSelectedClientIds([]);
      loadClients();
    } catch {
      toast("Erreur lors de la suppression groupée", "error");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleWipeDatabase = async () => {
    if (
      !confirm(
        "⚠️ ATTENTION: Voulez-vous vraiment vider ENTIÈREMENT la base de l'entreprise (fichiers physiques + vecteurs) ? Cette action est irréversible.",
      )
    )
      return;
    const validation = prompt(
      `Tapez "${selectedClient.name}" pour confirmer :`,
    );
    if (validation !== selectedClient.name) {
      toast("Validation échouée. Annulation.", "error");
      return;
    }
    try {
      await wipeDatabase(selectedId);
      toast(`Base de données vidée intégralement.`);
      loadDocuments(selectedId);
      loadClients();
    } catch {
      toast("Erreur lors du vidage", "error");
    }
  };

  const handleResyncDatabase = async () => {
    if (
      !confirm(
        "Re-synchroniser la base de données à partir des fichiers locaux ? Cela peut prendre du temps.",
      )
    )
      return;
    try {
      setUploading(true);
      const res = await resyncDatabase(selectedId);
      toast(
        `Re-synchronisation terminée : ${res.data.synced_files} fichier(s), ${res.data.total_chunks} chunks.`,
      );
      if (res.data.orphaned_docs && res.data.orphaned_docs.length > 0) {
        toast(
          `⚠️ Attention : ${res.data.orphaned_docs.length} document(s) orphelin(s) recréé(s) sans assignation (${res.data.orphaned_docs.join(", ")}). À assigner manuellement !`,
          "error",
        );
      }
      loadDocuments(selectedId);
      loadClients();
    } catch {
      toast("Erreur de synchronisation", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleViewChunks = async (source) => {
    try {
      const res = await fetchChunks(selectedId, source);
      setViewingChunks(res.data);
    } catch {
      toast("Erreur de récupération des chunks", "error");
    }
  };

  const handleDeleteDocument = async (source) => {
    if (
      !window.confirm(
        "Voulez-vous vraiment supprimer ce document ? Il sera déplacé vers la corbeille.",
      )
    )
      return;
    try {
      await deleteDocument(selectedId, source);
      toast("Document supprimé avec succès.");
      loadDocuments(selectedId);
      loadClients();
    } catch {
      toast("Erreur lors de la suppression du document", "error");
    }
  };

  const handleCancelDocument = async (source) => {
    if (
      !window.confirm(
        "Voulez-vous forcer l'arrêt du traitement en cours ? Cela passera le document en erreur pour libérer le serveur.",
      )
    )
      return;
    try {
      await cancelDocument(selectedId, source);
      toast("Traitement annulé avec succès.", "success");
      loadDocuments(selectedId);
    } catch (err) {
      toast(formatApiErrorDetail(err, "Erreur lors de l'annulation"), "error");
    }
  };

  const handleViewAllChunks = async () => {
    try {
      const res = await fetchDatabaseOverview();
      setViewingChunks({
        type: "overview",
        source: "Aperçu Global de la Base de Données",
        total_documents: res.data.total_documents,
        documents: res.data.documents,
      });
    } catch {
      toast("Erreur de récupération de la base totale", "error");
    }
  };

  const handleClearDatabase = async () => {
    if (
      !window.confirm(
        "Voulez-vous vraiment vider ENTIÈREMENT la base de données (ChromaDB + fichiers SQL) ? Cette action est irréversible !",
      )
    )
      return;
    try {
      await clearDatabase();
      toast("Base de données entièrement vidée.");
      setViewingChunks(null);
      loadClients();
    } catch {
      toast("Erreur lors du vidage de la base de données", "error");
    }
  };

  const handleResetStats = async () => {
    if (
      !window.confirm(
        "Voulez-vous vraiment réinitialiser toutes les statistiques (30 jours et Total) pour ce client ? Cette action est irréversible.",
      )
    )
      return;
    try {
      await resetClientMetrics(selectedId);
      toast("Statistiques réinitialisées.");
      loadClients();
    } catch {
      toast("Erreur lors de la réinitialisation", "error");
    }
  };

  const handleToggleClientPause = async (client, paused) => {
    try {
      await setClientPaused(client.id, paused);
      toast(
        paused
          ? `Client "${client.name}" mis en pause.`
          : `Client "${client.name}" réactivé.`,
      );
      await loadClients();
    } catch (err) {
      toast(
        formatApiErrorDetail(err, "Erreur changement du statut client"),
        "error",
      );
    }
  };

  const handleToggleLocalAgent = async (client, enabled) => {
    try {
      await updateClient(client.id, { local_agent_enabled: enabled });
      toast(
        enabled
          ? `Agent local autorisé pour « ${client.name} ».`
          : `Agent local désactivé pour « ${client.name} ».`,
      );
      await loadClients();
    } catch (err) {
      toast(
        formatApiErrorDetail(
          err,
          "Erreur lors du changement d’option agent local",
        ),
        "error",
      );
    }
  };

  if (!isAuth)
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4 selection:bg-brand-red/20 selection:text-brand-red">
        <Card className="max-w-[400px] w-full p-8 border-none shadow-xl">
          <form onSubmit={handleLogin}>
            <div className="size-12 rounded-xl flex items-center justify-center mb-6 overflow-hidden bg-stone-100">
              <img
                src="/raguia.webp"
                alt="RAG IA"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 mb-2">
              RAG Admin
            </h1>
            <p className="text-stone-500 mb-8 text-sm">
              Gestion de vos chatbots IA sur mesure
            </p>

            <div className="space-y-4 mb-8">
              <Input
                placeholder="Identifiant"
                value={creds.username}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, username: e.target.value }))
                }
                required
              />
              <Input
                type="password"
                placeholder="Mot de passe"
                value={creds.password}
                onChange={(e) =>
                  setCreds((p) => ({ ...p, password: e.target.value }))
                }
                required
              />
            </div>

            <Button
              type="submit"
              variant="black"
              className="w-full flex justify-center items-center gap-2"
            >
              Connexion
            </Button>
          </form>
        </Card>
      </div>
    );

  return (
    <div className="flex h-[100dvh] min-h-0 w-full max-w-[100vw] flex-col bg-stone-50 text-stone-800 overflow-hidden lg:flex-row">
      <Toast messages={toasts} onRemove={removeToast} />

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-stone-900/60 backdrop-blur-[2px] lg:hidden"
          aria-label="Fermer le menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-full w-[min(18rem,88vw)] max-w-[100vw] flex-col bg-stone-900 text-stone-300 shadow-2xl transition-transform duration-200 ease-out lg:static lg:z-20 lg:w-72 lg:max-w-none lg:shrink-0 lg:transtone-x-0 ${
          sidebarOpen ? "transtone-x-0" : "-transtone-x-full lg:transtone-x-0"
        }`}
      >
        <div className="flex items-center gap-3 border-b border-white/5 bg-stone-950/30 p-4 pb-4 sm:p-6 sm:pb-5">
          <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl">
            <img
              src="/raguia.webp"
              alt="RAG IA"
              className="h-full w-full object-contain"
            />
          </div>
          <h2 className="min-w-0 flex-1 truncate text-lg font-semibold tracking-wide text-white sm:text-xl">
            RAG Admin
          </h2>
          <button
            type="button"
            className="rounded-lg p-2 text-stone-400 hover:bg-white/10 hover:text-white lg:hidden"
            aria-label="Fermer le menu"
            onClick={() => setSidebarOpen(false)}
          >
            <PanelLeftClose size={22} />
          </button>
        </div>

        <div className="space-y-3 p-4 sm:p-5">
          <Button
            variant="brand"
            className="w-full flex h-11 items-center justify-center gap-2 rounded-xl font-medium"
            onClick={() => {
              setShowForm(true);
              setSelectedId(null);
              setIsEditing(false);
              setForm(buildNewForm(defaultConfig));
              setSidebarOpen(false);
            }}
          >
            <Plus size={18} /> Nouveau client
          </Button>
          <Button
            variant="black"
            className="w-full h-10 flex justify-center items-center gap-2 text-sm font-medium border border-white/5"
            onClick={handleViewAllChunks}
          >
            <Database size={16} /> Voir Toute la Base
          </Button>
          <Button
            variant="ghost"
            className="w-full h-10 bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/30 text-red-400 hover:text-red-300 rounded-xl flex justify-center items-center gap-2 text-sm font-medium transition-colors border border-red-500/20"
            onClick={handleClearDatabase}
          >
            <Trash2 size={16} /> Vider la Base
          </Button>
          <Button
            variant={currentView === "helicone" ? "black" : "ghost"}
            className={`w-full h-10 flex justify-center items-center gap-2 text-sm font-medium rounded-xl transition-colors ${
              currentView === "helicone"
                ? "border border-white/5"
                : "border border-white/10 text-stone-400 hover:text-white hover:bg-stone-800"
            }`}
            onClick={() => {
              setCurrentView(
                currentView === "helicone" ? "dashboard" : "helicone",
              );
              setSidebarOpen(false);
            }}
          >
            <BarChart3 size={16} />{" "}
            {currentView === "helicone"
              ? "Retour Dashboard"
              : "Métriques Helicone"}
          </Button>
        </div>

        {/* Barre de sélection multiple */}
        {clients.length > 0 && (
          <div className="px-4 pb-2 flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-stone-500 cursor-pointer hover:text-stone-300 transition-colors select-none">
              <input
                type="checkbox"
                className="size-3.5 rounded border-stone-600 bg-stone-800 text-blue-500 focus:ring-0 focus:ring-offset-0"
                checked={
                  selectedClientIds.length === clients.length &&
                  clients.length > 0
                }
                onChange={toggleAllClients}
              />
              {selectedClientIds.length === clients.length && clients.length > 0
                ? "Tout déselectionner"
                : "Tout sélectionner"}
            </label>
            {selectedClientIds.length > 0 && (
              <button
                className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 px-2.5 py-1.5 rounded-lg transition-all font-medium disabled:opacity-50"
                onClick={handleBulkDeleteClients}
                disabled={bulkDeleting}
                title={`Supprimer ${selectedClientIds.length} entreprise(s)`}
              >
                {bulkDeleting ? (
                  <svg
                    className="animate-spin size-3"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                ) : (
                  <Trash2 size={12} />
                )}
                Supprimer ({selectedClientIds.length})
              </button>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
          {clients.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              className={`group flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                selectedId === c.id
                  ? "bg-blue-600/10 text-blue-400 border border-blue-500/20"
                  : "hover:bg-stone-800/50 text-stone-400 hover:text-stone-200 border border-transparent"
              } ${
                selectedClientIds.includes(c.id)
                  ? "ring-1 ring-red-500/30 bg-red-500/5"
                  : ""
              }`}
              onClick={() => selectClient(c)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectClient(c);
                }
              }}
            >
              {/* Checkbox sélection */}
              <input
                type="checkbox"
                className="size-3.5 rounded border-stone-600 bg-stone-800 text-red-500 focus:ring-0 focus:ring-offset-0 shrink-0"
                checked={selectedClientIds.includes(c.id)}
                onChange={(e) => toggleClientSelection(e, c.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                className="size-2.5 rounded-full flex-shrink-0 shadow-sm"
                style={{ background: c.primary_color }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className={`font-medium truncate text-sm ${
                    selectedId === c.id
                      ? "text-blue-300"
                      : "text-stone-300 group-hover:text-white"
                  }`}
                >
                  {c.name}
                </div>
                <div className="text-xs text-stone-500 truncate mt-0.5">
                  /{c.slug} · {c.documents_count} chunks
                </div>
              </div>
              <button
                className="opacity-0 group-hover:opacity-100 p-1.5 text-stone-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all focus:outline-none shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(c.id, c.name);
                }}
                title="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {clients.length === 0 && (
            <p className="p-4 text-center text-sm text-stone-600 italic">
              Aucune entreprise
            </p>
          )}
        </nav>

        <div className="p-5 bg-stone-950/30 border-t border-white/5">
          <button
            className="w-full h-11 text-stone-400 hover:text-white hover:bg-stone-800 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-colors"
            onClick={() => {
              clearTokens();
              setIsAuth(false);
            }}
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-stone-50">
        <div className="z-10 flex shrink-0 items-center gap-2 border-b border-stone-200 bg-white px-3 py-2 shadow-sm sm:px-4 lg:hidden">
          <button
            type="button"
            className="flex size-10 shrink-0 items-center justify-center rounded-xl text-stone-700 hover:bg-stone-100"
            aria-label="Ouvrir le menu"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={22} />
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-stone-800">
            {selectedClient?.name || "RAG Admin"}
          </span>
        </div>

        {/* Dashboard Global - Affiché en haut de la zone principale */}
        <div className="z-10 border-b border-stone-200 bg-white p-3 shadow-sm sm:px-4 sm:py-4 lg:px-6">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <h1 className="flex flex-wrap items-center gap-2 text-base font-semibold text-stone-800 sm:text-lg lg:text-xl">
              <Activity className="shrink-0 text-brand-red" size={20} />{" "}
              <span className="leading-tight">Dashboard Global</span>{" "}
              <Badge variant="outline" className="text-[10px] sm:text-xs">
                30 derniers jours
              </Badge>
            </h1>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 lg:grid-cols-8">
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Requêtes
                </span>
                <span className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg">
                  <MessageSquare size={14} className="text-brand-red" />
                  {(globalStats.stats_30d?.queries || 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Tokens (E)
                </span>
                <span
                  className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg"
                  title="Tokens d'entrée (prompts)"
                >
                  <Cpu size={14} className="text-blue-400" />
                  {(globalStats.stats_30d?.tokens_in || 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Tokens (S)
                </span>
                <span
                  className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg"
                  title="Tokens de sortie (réponses)"
                >
                  <Cpu size={14} className="text-blue-600" />
                  {(globalStats.stats_30d?.tokens_out || 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  OCR
                </span>
                <span className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg">
                  <Scan size={14} className="text-emerald-500" />
                  {(globalStats.stats_30d?.ocr || 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Embeddings
                </span>
                <span className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg">
                  <Layers size={14} className="text-amber-500" />
                  {(globalStats.stats_30d?.embeddings || 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Stockage
                </span>
                <span className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg">
                  <HardDrive size={14} className="text-stone-500" />
                  {formatSize(
                    clients.reduce(
                      (acc, c) => acc + (c.current_storage_bytes || 0),
                      0,
                    ),
                  )}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Msg. in (30j)
                </span>
                <span
                  className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg"
                  title="Messages utilisateur (questions) — 30 derniers jours"
                >
                  <MessageSquare size={14} className="text-violet-500" />
                  {(globalStats.stats_30d?.messages_in ?? 0).toLocaleString()}
                </span>
              </Card>
              <Card className="flex flex-col px-2 py-1.5 shadow-sm sm:px-3 sm:py-2 bg-stone-50 border-stone-200">
                <span className="mb-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-500 sm:text-[10px]">
                  Réponses out (30j)
                </span>
                <span
                  className="flex items-center gap-1 text-sm font-bold leading-none text-stone-800 sm:gap-1.5 sm:text-lg"
                  title="Réponses assistant — 30 derniers jours"
                >
                  <MessageSquare size={14} className="text-fuchsia-500" />
                  {(globalStats.stats_30d?.messages_out ?? 0).toLocaleString()}
                </span>
              </Card>
            </div>
          </div>
        </div>

        {currentView === "helicone" ? (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 lg:p-8">
            <HeliconeMetrics />
          </div>
        ) : (
          <>
        {showForm && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 lg:p-8">
            <Card className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center justify-between gap-4 mb-8 pb-6 border-b border-stone-100">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-stone-800">
                  <Plus size={22} className="text-brand-red" /> Créer une nouvelle
                  entreprise
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-stone-400 hover:text-stone-600"
                  onClick={() => setShowForm(false)}
                >
                  <X size={20} />
                </Button>
              </div>
              <form onSubmit={handleCreate} className="space-y-8">
                <ClientFormFields
                  form={form}
                  onChange={handleFormChange}
                  isNew={true}
                  advancedOpen={advancedOpen}
                  setAdvancedOpen={setAdvancedOpen}
                  defaultConfig={defaultConfig}
                />
                <div className="flex items-center justify-end gap-3 pt-6 border-t border-stone-100 mt-8">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="brand"
                    disabled={submitting}
                    className="flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin size-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        Création…
                      </>
                    ) : (
                      <>
                        <Plus size={18} /> Créer l'entreprise
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {selectedClient && !showForm && (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-4 sm:space-y-6 sm:p-6 lg:p-8">
            <Card className="p-4 sm:p-6 lg:p-8">
              <div className="mb-6 flex flex-col gap-4 border-b border-stone-100 pb-6 sm:flex-row sm:items-center sm:justify-between">
                <h2
                  className="flex items-center gap-2 text-xl font-bold sm:gap-3 sm:text-2xl"
                  style={{ color: selectedClient.primary_color }}
                >
                  {selectedClient.logo_url && (
                    <img
                      src={selectedClient.logo_url}
                      alt=""
                      className="h-10 w-auto object-contain rounded-lg shadow-sm"
                    />
                  )}
                  {selectedClient.name}
                </h2>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  <a
                    href={`/portal/${selectedClient.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-700 shadow-sm transition-all hover:bg-stone-50 sm:justify-start"
                  >
                    <ExternalLink
                      size={16}
                      className="shrink-0 text-stone-400"
                    />
                    Portail entreprise
                  </a>
                  <a
                    href={`/c/${selectedClient.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-700 shadow-sm transition-all hover:bg-stone-50 sm:justify-start"
                  >
                    <ExternalLink
                      size={16}
                      className="shrink-0 text-stone-400"
                    />
                    Tester le chatbot
                  </a>
                  {!isEditing ? (
                    <Button
                      variant="brand"
                      className="gap-2"
                      style={brandStyle(selectedClient)}
                      onClick={() => {
                        setForm({ ...selectedClient });
                        setIsEditing(true);
                      }}
                    >
                      <Edit3 size={16} /> Modifier
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                      onClick={handleUpdate}
                    >
                      <Check size={16} /> Sauvegarder
                    </Button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <ClientFormFields
                  form={form}
                  onChange={handleFormChange}
                  isNew={false}
                  advancedOpen={advancedOpen}
                  setAdvancedOpen={setAdvancedOpen}
                  defaultConfig={defaultConfig}
                />
              ) : (
                <ClientReadView
                  client={selectedClient}
                  onResetStats={handleResetStats}
                  onTogglePause={handleToggleClientPause}
                  onToggleLocalAgent={handleToggleLocalAgent}
                />
              )}
            </Card>

            <Card className="p-4 sm:p-6 lg:p-8 mt-6">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <h3 className="flex items-center gap-2 text-base font-semibold text-stone-800 sm:text-lg">
                  <Database size={20} className="shrink-0 text-brand-red" />{" "}
                  <span>Base de données Vectorielle</span>
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={handleResyncDatabase}
                    disabled={uploading}
                  >
                    <RefreshCw
                      size={16}
                      className={
                        uploading
                          ? "animate-spin text-brand-red"
                          : "text-stone-400"
                      }
                    />{" "}
                    Sync Base
                  </Button>
                  <Button
                    variant="ghost"
                    className="gap-2 bg-red-50 hover:bg-red-100 text-red-600"
                    onClick={handleWipeDatabase}
                    disabled={uploading}
                  >
                    <AlertTriangle size={16} /> Vider Base
                  </Button>
                </div>
              </div>

              {documents.length > 0 ? (
                <div className="mt-6 border border-stone-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="bg-stone-50 p-4 border-b border-stone-200 flex flex-wrap items-center justify-between text-sm gap-4">
                    <span className="font-medium text-stone-700">
                      {documents.length} document(s) indexé(s)
                    </span>
                  </div>
                  <div className="divide-y divide-stone-100 max-h-[500px] overflow-y-auto custom-scrollbar">
                    {documents.map((doc) => (
                      <div
                        key={doc.source}
                        className="flex flex-col gap-3 p-4 transition-colors hover:bg-stone-50 sm:flex-row sm:items-center sm:gap-4"
                      >
                        <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500">
                            <FileText size={20} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-stone-900 font-medium flex items-center gap-2 truncate">
                              <span className="truncate">{doc.source}</span>
                              {doc.status === "processing" ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                                  <Loader2 size={10} className="animate-spin" />{" "}
                                  En cours
                                </span>
                              ) : doc.status === "error" ? (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 shrink-0 cursor-help"
                                  title={
                                    doc.error_message || "Erreur d'analyse"
                                  }
                                >
                                  <AlertCircle size={10} /> Erreur
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                                  <CheckCircle2 size={10} /> Prêt
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5 text-xs text-stone-500">
                              {doc.chunks} chunks extraits
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center justify-end gap-1 border-t border-stone-100 pt-3 sm:border-t-0 sm:pt-0">
                          {doc.status === "processing" && (
                            <button
                              className="p-2 text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Forcer l'arrêt du traitement"
                              onClick={() => handleCancelDocument(doc.source)}
                            >
                              <Ban size={18} />
                            </button>
                          )}
                          <button
                            className="p-2 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir les chunks extraits"
                            onClick={() => handleViewChunks(doc.source)}
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer le document"
                            onClick={() => handleDeleteDocument(doc.source)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-8 text-center p-8 border border-dashed border-stone-200 rounded-2xl bg-stone-50/50">
                  <p className="text-stone-500">
                    Aucun document indexé pour cette entreprise.
                  </p>
                </div>
              )}
            </Card>
          </div>
        )}

        {!selectedClient && !showForm && (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-4 sm:space-y-6 sm:p-6 lg:p-8">
            <Card className="flex flex-col gap-4 p-4 sm:gap-6 sm:p-6 lg:p-8">
              <h2 className="text-2xl font-semibold flex items-center gap-3 text-stone-800">
                <Database className="text-brand-red" />
                Tableau de bord global
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 mt-4">
                <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-blue-300 transition-colors">
                  <div className="size-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-sm">
                    <Users size={28} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
                      Entreprises
                    </div>
                    <div className="text-3xl font-bold text-stone-900 mt-1">
                      {clients.length}
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-brand-red/30 transition-colors">
                  <div className="size-14 bg-brand-red/10 text-brand-red rounded-2xl flex items-center justify-center shadow-sm">
                    <FolderOpen size={28} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
                      Équipes
                    </div>
                    <div className="text-3xl font-bold text-stone-900 mt-1">
                      {clients.reduce(
                        (sum, c) => sum + (c.subclients_count || 0),
                        0,
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-amber-300 transition-colors">
                  <div className="size-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center shadow-sm shrink-0">
                    <HardDrive size={28} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
                      Stockage
                    </div>
                    <div className="text-2xl font-bold text-stone-900 mt-1 whitespace-nowrap">
                      {formatSize(
                        clients.reduce(
                          (sum, c) => sum + (c.current_storage_bytes || 0),
                          0,
                        ),
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-emerald-300 transition-colors">
                  <div className="size-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-sm">
                    <FileText size={28} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
                      Total Chunks
                    </div>
                    <div className="text-3xl font-bold text-stone-900 mt-1">
                      {clients.reduce(
                        (sum, c) => sum + (c.documents_count || 0),
                        0,
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-stone-200 shadow-sm p-6 rounded-2xl flex items-center gap-5 hover:border-rose-300 transition-colors">
                  <div className="size-14 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center shadow-sm">
                    <AlertTriangle size={28} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-500 tracking-wide uppercase">
                      Erreurs / Logs
                    </div>
                    <div className="text-3xl font-bold text-stone-900 mt-1">
                      {errorCount}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-stone-100 pt-8">
                <h3 className="text-lg font-semibold text-stone-800 mb-4">
                  Actions rapides
                </h3>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="brand"
                    className="h-12 px-6 gap-2"
                    onClick={() => {
                      setShowForm(true);
                      setSelectedId(null);
                      setIsEditing(false);
                      setForm(
                        defaultConfig
                          ? { ...EMPTY_FORM, ...defaultConfig }
                          : EMPTY_FORM,
                      );
                    }}
                  >
                    <Plus size={20} /> Nouvelle entreprise
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 px-6 gap-2"
                    onClick={handleViewAllChunks}
                  >
                    <Database size={20} /> Analyser toute la base
                  </Button>
                </div>
              </div>

              {/* ── DuckDB Analytics ── */}
              <div className="border-t border-stone-100 pt-8">
                <AdminAnalytics />
              </div>
            </Card>
          </div>
        )}

        {viewingChunks && (
          <div
            className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
            onClick={() => setViewingChunks(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col ring-1 ring-stone-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-stone-100">
                <h3 className="text-xl font-semibold flex flex-wrap items-center gap-2 text-stone-800">
                  <Eye size={22} className="text-brand-red" />{" "}
                  {viewingChunks.source}
                  {viewingChunks.type === "overview" ? (
                    <Badge variant="outline" className="bg-brand-red/10 text-brand-red border-brand-red/20">
                      {viewingChunks.total_documents} documents indexés
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-brand-red/10 text-brand-red border-brand-red/20">
                      {viewingChunks.total_chunks} chunks dans ChromaDB
                    </Badge>
                  )}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-stone-400 hover:text-stone-600"
                  onClick={() => setViewingChunks(null)}
                >
                  <X size={24} />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-stone-50 rounded-b-2xl custom-scrollbar space-y-4">
                {viewingChunks.type === "overview"
                  ? viewingChunks.documents?.map((doc, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-xl p-5 shadow-sm border border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                      >
                        <div>
                          <div className="font-semibold text-stone-800 text-lg">
                            {doc.source}
                          </div>
                          <div
                            className="mt-2 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold"
                            style={{
                              background: doc.collection_color
                                ? `${doc.collection_color}15`
                                : undefined,
                              color: doc.collection_color || undefined,
                              border: `1px solid ${doc.collection_color || "#e2e8f0"}`,
                            }}
                          >
                            {doc.collection}
                          </div>
                        </div>
                        <div className="text-left sm:text-right">
                          <div className="font-bold text-stone-700">
                            {doc.chunks} chunks
                          </div>
                          <div className="text-sm text-stone-500 mt-1">
                            {doc.extension} • {doc.type}
                          </div>
                        </div>
                      </div>
                    ))
                  : viewingChunks.chunks?.map((chunk, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden"
                      >
                        <div className="bg-stone-50 border-b border-stone-200 px-4 py-3 flex items-center gap-3">
                          <span className="font-semibold text-stone-700">
                            Chunk #{idx + 1}
                          </span>
                          {chunk.metadata?.type && (
                            <span className="bg-white border border-stone-200 text-stone-600 text-xs px-2 py-1 rounded-md shadow-sm">
                              {chunk.metadata.type}
                            </span>
                          )}
                        </div>
                        <pre className="p-4 text-sm text-stone-700 whitespace-pre-wrap font-mono leading-relaxed bg-white overflow-x-auto">
                          {chunk.text}
                        </pre>
                      </div>
                    ))}
                {viewingChunks.total_chunks === 0 && (
                  <p className="text-center p-8 bg-white rounded-xl border border-dashed border-stone-300 text-stone-500">
                    Aucun texte extrait n'a été trouvé.
                  </p>
                )}
                {viewingChunks.total_documents === 0 && (
                  <p className="text-center p-8 bg-white rounded-xl border border-dashed border-stone-300 text-stone-500">
                    La base de données est complètement vide.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

          </>
        )}

        {uploading && (
          <LoadingOverlay
            title="Analyse en cours..."
            description="Nous convertissons vos documents et extrayons le texte via Mistral AI. Cela peut prendre 1 à 2 minutes pour les fichiers volumineux."
          />
        )}
        <footer className="border-t border-stone-200 py-3 text-center">
          <a
            href="https://status.valentin-fiess.fr/status/raguia"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-stone-500 hover:text-stone-700 underline"
          >
            Statut du service
          </a>
        </footer>
      </main>
    </div>
  );
}
